import { afterNextRender, Component, computed, inject, input, InputSignal } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { tap } from 'rxjs';
import { AccountService } from '../../account.service';
import { AuthorizeService } from '../../purchase/authorize/authorize.service';
import { ANetPayload, ANetResponse } from '../../purchase/authorize/authorize.types';
import { LoaderSizeEnum } from '../../section-loader/section-loader.types';
import { PaymentDataPayload } from '../../types/account.types';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { OnlyNumberDirective } from '../../directives/only-number.directive';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ExplorerPaymentMethodService } from './explorer-payment-method.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ResolutionService } from '../../resolution.service';
import { KLAVIYOEVENTS } from '../../klaviyo/events';
import { Session } from '../../types/session.type';
import { LOCALSTORAGE_KEYS } from '../../constants/databases';
import { LocalStorageService } from '../../local-storage.service';
import { KlaviyoService } from '../../klaviyo/klaviyo.service';
@Component({
  selector: 'app-explorer-payment-method',
  imports: [CommonModule, ReactiveFormsModule, MatSlideToggleModule, MatCheckboxModule, OnlyNumberDirective, RouterLink],
  templateUrl: './explorer-payment-method.component.html',
  styleUrl: './explorer-payment-method.component.scss'
})
export class ExplorerPaymentMethodComponent {
  public explorerPaymentMethodService = inject(ExplorerPaymentMethodService);
  private authorizeService = inject(AuthorizeService);
  private accountService = inject(AccountService);
  private activeModal = inject(NgbActiveModal);
  private resolutionService = inject(ResolutionService);
  private localStorageService = inject(LocalStorageService);
  private klaviyoService = inject(KlaviyoService);

  states = computed(() => this.explorerPaymentMethodService.getStates());
  deliveryDayText = computed(() => this.explorerPaymentMethodService.getDeliveryDayText());
  title = computed(() => this.explorerPaymentMethodService.getTitle());

  isMobile = computed(() => this.resolutionService.isMobile());


  type: InputSignal<'subscription' | 'checkout'> = input<'subscription' | 'checkout'>('subscription');

  LoaderSizeEnum = LoaderSizeEnum;

  constructor() {
    // Aplicar validadores a los campos de billing address (siempre requeridos)
    this.applyBillingValidators(true);
    afterNextRender(() => {
      this.explorerPaymentMethodService.setTitle(this.type());
    });
  }


  private applyBillingValidators(enabled: boolean) {
    const controls = ['billingName', 'billingAddress1', 'billingCity', 'billingState', 'billingZip'];
    controls.forEach(cn => {
      const c = this.explorerPaymentMethodService.paymentForm.get(cn);
      if (!c) return;
      if (enabled) c.addValidators(Validators.required); else c.removeValidators(Validators.required);
      c.updateValueAndValidity({ emitEvent: false });
    });
  }

  invalid(name: string) { const c = this.explorerPaymentMethodService.paymentForm.get(name); return !!c && c.invalid && (c.dirty || c.touched); }

  // --- Card brand detection and formatting ---
  cardBrand: 'visa' | 'mastercard' | 'amex' | 'discover' | 'unknown' = 'unknown';

  onCardNumberInput(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const digits = (input.value || '').replace(/\D+/g, '');
    this.cardBrand = this.detectBrand(digits);
    const formatted = this.formatCardNumber(digits, this.cardBrand);
    // Update input and form control without moving cursor management for simplicity
    input.value = formatted;
    this.explorerPaymentMethodService.paymentForm.get('cardNumber')?.setValue(formatted, { emitEvent: false });
  }

  private detectBrand(num: string): 'visa' | 'mastercard' | 'amex' | 'discover' | 'unknown' {
    if (/^4/.test(num)) return 'visa';
    if (/^(5[1-5]|2(2[2-9][1-9]|2[3-9]\d|[3-6]\d{2}|7[01]\d|720))/.test(num)) return 'mastercard';
    if (/^(34|37)/.test(num)) return 'amex';
    if (/^(6011|65|64[4-9]|622(12[6-9]|1[3-9]\d|[2-8]\d{2}|9([01]\d|2[0-5])))/.test(num)) return 'discover';
    return 'unknown';
  }

  private formatCardNumber(num: string, brand: string): string {
    if (!num) return '';
    if (brand === 'amex') {
      return num
        .slice(0, 15)
        .replace(/^(\d{0,4})(\d{0,6})(\d{0,5}).*/, (m, g1, g2, g3) => [g1, g2, g3].filter(Boolean).join(' '));
    }
    // default: groups of 4 up to 19 chars with spaces
    return num
      .slice(0, 19)
      .replace(/(\d{4})(?=\d)/g, '$1 ') // insert spaces after each 4 digits when more digits follow
      .trim();
  }

  // Resolve card brand icon path (SVG in assets)
  cardBrandIcon(): string | null {
    switch (this.cardBrand) {
      case 'visa': return '/assets/images/credit-card/visa.svg';
      case 'mastercard': return '/assets/images/credit-card/master-card.svg';
      case 'amex': return '/assets/images/credit-card/american-express.svg';
      case 'discover': return '/assets/images/credit-card/discover.svg';
      default: return null;
    }
  }

  // --- Expiration date MM/YYYY formatting & input restrictions ---
  onExpKeyDown(ev: KeyboardEvent) {
    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (allowed.includes(ev.key)) return;
    if (/^[0-9]$/.test(ev.key)) return;
    ev.preventDefault();
  }

  onExpInput(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const digits = (input.value || '').replace(/\D+/g, '').slice(0, 6); // MMYYYY
    const mm = digits.slice(0, 2);
    const yyyy = digits.slice(2, 6);
    const formatted = yyyy ? `${mm}/${yyyy}` : mm;
    input.value = formatted;
    this.explorerPaymentMethodService.paymentForm.get('exp')?.setValue(formatted, { emitEvent: false });
  }

  onExpPaste(ev: ClipboardEvent) {
    ev.preventDefault();
    const pasted = ev.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D+/g, '').slice(0, 6);
    const mm = digits.slice(0, 2);
    const yyyy = digits.slice(2, 6);
    const input = ev.target as HTMLInputElement;
    const formatted = yyyy ? `${mm}/${yyyy}` : mm;
    input.value = formatted;
    this.explorerPaymentMethodService.paymentForm.get('exp')?.setValue(formatted, { emitEvent: false });
  }

  // --- Accept.js Integration ---
  sendDataToANet() {
    const formValue = this.explorerPaymentMethodService.paymentForm.value;
    const [month, year] = formValue.exp?.split('/') || ['', ''];

    const ANetData: ANetPayload = {
      cardCode: formValue.cvc || '',
      cardNumber: formValue.cardNumber?.replace(/\s/g, '') || '',
      year,
      month
    };

    this.authorizeService.sendPaymentDataToAnet(ANetData, (response: ANetResponse) =>
      this.addPaymentMethod(response)
    );
  }

  private addPaymentMethod(opaqueData: ANetResponse) {
    const payload = this.setUpPaymentMethodPayload(opaqueData);
    this.accountService
      .addPaymentMethod(payload)
      .pipe(
        tap(res => {
          if (res?.data) {
            setTimeout(() => {
              const session: Session | null = this.localStorageService.get(LOCALSTORAGE_KEYS.SESSION);
              if (!session) return;
              const email = session.accountInfo.email;
              this.klaviyoService.trackSignupEvent(KLAVIYOEVENTS.RegistrationCompleted, {}, email);
              this.activeModal.close({ paymentMethodCreated: true });
            }, 1);
          }
        })
      )
      .subscribe();
  }

  private setUpPaymentMethodPayload(opaqueData: ANetResponse): PaymentDataPayload {
    const formValue = this.explorerPaymentMethodService.paymentForm.value;
    const cardNumber = formValue.cardNumber?.replace(/\s/g, '') || '';
    const cardLastNumbers = cardNumber.substring(cardNumber.length - 4, cardNumber.length);

    return {
      billingAddress: {
        additional: formValue.billingAddress2 || '',
        city: formValue.billingCity || '',
        stateCode: formValue.billingState || '',
        street: formValue.billingAddress1 || '',
        zip: formValue.billingZip || ''
      },
      card: {
        firstName: formValue.billingName?.split(' ')[0] || '',
        lastName: formValue.billingName?.split(' ').slice(1).join(' ') || '',
        expirationDate: formValue.exp?.split('/').reverse().join('-') || '',
        number: cardLastNumbers,
      },
      opaqueData
    };
  }
}
